import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
} from '@angular/forms';
import { ToastService } from '../../../../../../services/toast.service';
import { CompanyService } from '../../../../../../services/company/company.service';
import { CommonService } from '../../../../../../services/common/common.service';
import { AuthService } from '../../../../../../services/auth.service';
import { ValidationService } from '../../../../../../services/validation/validation.service';
import { FormChangeTrackerService } from '../../../../../../services/form-change-tracker.service';
import { Location } from '@angular/common';
import { FieldMappingService } from '../../../../../../services/field-mapping/field-mapping.service';

// Standard field keys pre-populated as fixed rows (non-removable)
const FIXED_STANDARD_KEYS = [
  'main_array_key',
  'employeecode',
  'date',
  'checkin_time',
  'checkout_time',
  'punch_array',
];
// Keys that do NOT need a date/time format dropdown
const NO_FORMAT_KEYS = [
  'employeecode',
  'main_array_key',
  'punch_array',
  'tna_io',
  'punch_csv',
];

@Component({
  selector: 'app-add-field-mapping',
  templateUrl: './add-field-mapping.component.html',
  styleUrl: './add-field-mapping.component.scss',
})
export class AddFieldMappingComponent implements OnInit, OnDestroy {
  formdata!: FormGroup;
  formKey: string = 'fieldMappingFormKey';
  formisDisable: boolean = false;
  saveClick: boolean = false;

  paramValue: any = '';
  viewData: any = '';
  hideCompany: boolean = false;
  selectedItem: any = null;
  companyList: any = [];

  // ── Option Lists (exposed to template) ──────────────────────────────────────
  fieldFormatOptions = [
    { label: 'Date (YYYY-MM-DD)', value: 'YYYY-MM-DD' },
    { label: 'Date (DD-MM-YYYY)', value: 'DD-MM-YYYY' },
    { label: 'Date (MM-DD-YYYY)', value: 'MM-DD-YYYY' },
    { label: 'Date (DD/MM/YYYY)', value: 'DD/MM/YYYY' },
    { label: 'Date (MM/DD/YYYY)', value: 'MM/DD/YYYY' },
    { label: 'Date (YYYY/MM/DD)', value: 'YYYY/MM/DD' },
    { label: 'Time (HH:mm)', value: 'HH:mm' },
    { label: 'Time (hh:mm A)', value: 'hh:mm A' },
    { label: 'Time with Seconds (HH:mm:ss)', value: 'HH:mm:ss' },
    { label: 'Time with Seconds (hh:mm:ss A)', value: 'hh:mm:ss A' },
    { label: 'DateTime (YYYY-MM-DD HH:mm)', value: 'YYYY-MM-DD HH:mm' },
    { label: 'DateTime (YYYY-MM-DD hh:mm A)', value: 'YYYY-MM-DD hh:mm A' },
    { label: 'DateTime (DD-MM-YYYY HH:mm:ss)', value: 'DD-MM-YYYY HH:mm:ss' },
    { label: 'DateTime (DD/MM/YYYY hh:mm A)', value: 'DD/MM/YYYY hh:mm A' },
  ];

  typeOption = [{ label: 'Attendance', value: 'Attendance' }];

  httpMethodOptions = [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
  ];

  authTypeOptions = [
    { label: 'None', value: null },
    { label: 'Bearer Token', value: 'bearer' },
    { label: 'Basic Auth', value: 'basic' },
    { label: 'API Key (Header)', value: 'api_key' },
  ];

  scheduleTypeOptions = [
    { label: 'Every N Minutes', value: 'every_n_minutes' },
    { label: 'Every N Hours', value: 'every_n_hours' },
    { label: 'Fixed Times of Day', value: 'fixed_times' },
    { label: 'Custom Cron Expression', value: 'custom_cron' },
  ];

  optionalFieldKeyOptions = [
    { label: 'IN/OUT Direction (Scenario A – Explicit flag)', value: 'tna_io' },
    {
      label: 'Punch Array (Scenario B – Multiple timestamps)',
      value: 'punch_array',
    },
    {
      label: 'Punch CSV (Scenario C – Comma-separated times)',
      value: 'punch_csv',
    },
  ];

  // Keys for which format column shows '---'
  showIfenArray: any = NO_FORMAT_KEYS;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly companyService: CompanyService,
    private readonly commonService: CommonService,
    private readonly auth: AuthService,
    private readonly validation: ValidationService,
    private readonly formChangeService: FormChangeTrackerService,
    private readonly fieldMappingService: FieldMappingService,
    private readonly location: Location,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.getCompanyList();
    this.setupComponent();
    this.setupFormChangeTracking();
    this.updateFieldMappingBasedOnPunch(1);
    this.formdata.get('single_punch')?.valueChanges.subscribe((checked) => {
      this.updateFieldMappingBasedOnPunch(checked);
    });
  }

  ngOnDestroy(): void {
    this.formChangeService.unregisterForm(this.formKey);
  }

  private initializeForm(): void {
    this.formdata = this.fb.group({
      // ── Basic Info ──────────────────────────────────────────────────────────
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(this.validation.letterNumberContinueSpace),
        ],
      ],
      type: ['Attendance', [Validators.required]],
      status: [1],
      single_punch: [1],

      // ── Company ─────────────────────────────────────────────────────────────
      company: [null, [Validators.required]],

      // ── API Connection ───────────────────────────────────────────────────────
      api_url: [
        '',
        [Validators.required, Validators.pattern(/^https?:\/\/.+/)],
      ],
      http_method: ['GET'],
      api_key: [''],
      branch_name: [''],
      location_name: [''],

      // ── Auth ─────────────────────────────────────────────────────────────────
      auth_type: [null],
      auth_token: [''],
      auth_username: [''],
      auth_password: [''],
      auth_key_value: [''],
      auth_header_name: [''],

      // ── Schedule ─────────────────────────────────────────────────────────────
      schedule_type: [null],
      schedule_minutes: [5, [Validators.min(1), Validators.max(59)]],
      schedule_hours: [1, [Validators.min(1), Validators.max(23)]],
      schedule_cron: [''],
      schedule_times: [[]],
      schedule_time_input: [''],

      // ── Field Mapping ─────────────────────────────────────────────────────────
      field_mapping: this.fb.array([]),
      optional_field_mapping: this.fb.array([]),

      // ── Value Translation ─────────────────────────────────────────────────────
      value_mapping: this.fb.array([]),

      // ── Device IDs ────────────────────────────────────────────────────────────
      device_ids: [[], [Validators.required]],
      device_id_input: ['', [Validators.pattern(/^\S+$/)]],

      // Shared helper input for chip fields
      company_field_key_input: ['', [Validators.pattern(/^\S+$/)]],
      optional_field_key_input: ['', [Validators.pattern(/^\S+$/)]],
    });
  }

  get field_mapping_array(): FormArray {
    return this.formdata.get('field_mapping') as FormArray;
  }

  get optional_field_mapping_array(): FormArray {
    return this.formdata.get('optional_field_mapping') as FormArray;
  }

  get value_mapping_array(): FormArray {
    return this.formdata.get('value_mapping') as FormArray;
  }

  get optionalFieldKeyInput(): FormControl {
    return this.formdata.get('optional_field_key_input') as FormControl;
  }

  /** Alias used by the template binding `[formControl]="optionalFieldKeyInputControl"` */
  get optionalFieldKeyInputControl(): FormControl {
    return this.optionalFieldKeyInput;
  }

  get companyFieldKeyInput(): FormControl {
    return this.formdata.get('company_field_key_input') as FormControl;
  }

  /** True when an optional tna_io row exists (controls value_mapping visibility) */
  get hasTnaIoRow(): boolean {
    return this.optional_field_mapping_array.controls.some(
      (c) => c.get('standard_field_key')?.value === 'tna_io',
    );
  }

  get authType(): string | null {
    return this.formdata.get('auth_type')?.value ?? null;
  }

  get scheduleType(): string | null {
    return this.formdata.get('schedule_type')?.value ?? null;
  }

  addValueMapping(): void {
    this.value_mapping_array.push(
      this.fb.group({
        vendor_value: ['', Validators.required],
        system_value: ['', Validators.required],
      }),
    );
  }

  removeValueMapping(index: number): void {
    this.value_mapping_array.removeAt(index);
  }

  private setupComponent(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.paramValue = id;
        this.getViewData(id);
      } else {
        this.paramValue = null;
      }
    });

    // Always run these
    // this.setupUserPermissions();
  }

  // private setupUserPermissions(): void {
  //   const userData: any = this.commonService.getLocalStorageData();
  //   if (userData) {
  //     if (!userData.client) {
  //       this.hideCompany = true;
  //       this.formdata.patchValue({
  //         company_id: userData.companyID
  //       });
  //     }
  //   }
  // }

  private setupFormChangeTracking(): void {
    this.formdata.valueChanges.subscribe(() => {
      this.formisDisable = this.formChangeService.hasChanged(
        this.formKey,
        this.formdata,
      );
    });
  }

  getCompanyList(): void {
    this.companyService.getClientCompanyList().subscribe({
      next: (resp: any) => {
        this.companyList = resp.data;
      },
      error: (err) => {
        console.error('Error fetching company list:', err);
      },
    });
  }

  // ─── Schedule Times ───────────────────────────────────────────────────────────

  addScheduleTime(): void {
    const input = this.formdata.get('schedule_time_input');
    const value = (input?.value as string)?.trim();
    if (!value) return;
    const times: string[] = [
      ...(this.formdata.get('schedule_times')?.value ?? []),
    ];
    if (!times.includes(value)) {
      times.push(value);
      this.formdata.get('schedule_times')?.setValue(times);
      this.formdata.get('schedule_times')?.markAsDirty();
    }
    input?.reset();
  }

  removeScheduleTime(index: number): void {
    const times: string[] = [
      ...(this.formdata.get('schedule_times')?.value ?? []),
    ];
    times.splice(index, 1);
    this.formdata.get('schedule_times')?.setValue(times);
  }

  // ─── Optional Field Mapping Rows ─────────────────────────────────────────────

  addOptionalFieldRow(standardKey = ''): void {
    const isPunchArray = standardKey === 'punch_array';
    this.optional_field_mapping_array.push(
      this.fb.group({
        standard_field_key: [standardKey, Validators.required],
        company_field_key: [isPunchArray ? [] : '', Validators.required],
        company_field_key_format: [null],
        standard_field_key_format: [null],
        company_field_key_delimiter: [','],
      }),
    );
  }

  removeOptionalFieldRow(index: number): void {
    this.optional_field_mapping_array.removeAt(index);
  }

  onOptionalKeyChange(rowIndex: number): void {
    const row = this.optional_field_mapping_array.at(rowIndex) as FormGroup;
    const key = row.get('standard_field_key')?.value;
    row.get('company_field_key')?.setValue(key === 'punch_array' ? [] : '');
  }

  addOptionalPunchItem(rowIndex: number): void {
    const row = this.optional_field_mapping_array.at(rowIndex) as FormGroup;
    if (!row) return;
    const inputControl = this.formdata.get(
      'optional_field_key_input',
    ) as FormControl;
    const value = (inputControl?.value as string)?.trim();
    if (!value) {
      this.toastService.failure('Please enter a value');
      return;
    }
    if (inputControl?.invalid) {
      this.toastService.failure('Invalid format: no spaces allowed');
      return;
    }
    const arr: string[] = [...(row.get('company_field_key')?.value ?? [])];
    if (!arr.includes(value)) {
      arr.push(value);
      row.get('company_field_key')?.setValue(arr);
      row.get('company_field_key')?.markAsDirty();
    } else {
      this.toastService.failure('Value already exists');
    }
    this.formdata.get('optional_field_key_input')?.reset();
  }

  removeOptionalPunchItem(rowIndex: number, itemIndex: number): void {
    const row = this.optional_field_mapping_array.at(rowIndex) as FormGroup;
    if (!row) return;
    const arr: string[] = [...(row.get('company_field_key')?.value ?? [])];
    arr.splice(itemIndex, 1);
    row.get('company_field_key')?.setValue(arr);
    row.get('company_field_key')?.markAsTouched();
  }

  getViewData(id: any): void {
    this.fieldMappingService.getFieldMappingDataById(id).subscribe({
      next: async (response) => {
        const data = response.data;
        this.viewData = data;

        // ── Patch top-level fields ────────────────────────────────────────────
        this.formdata.patchValue({
          name: data.name || '',
          type: data.type || '',
          status: data.status || 0,
          single_punch: data.single_punch || 0,
          device_ids: data.device_ids || [],
          company: data.company?.id ?? null,
          api_url: data.api_url || '',
          http_method: data.http_method || 'GET',
          api_key: data.api_key || '',
          branch_name: data.branch_name || '',
          location_name: data.location || '',
          auth_type: data.auth_type ?? null,
          schedule_type: data.schedule_type ?? null,
        });

        // ── Parse auth_config ─────────────────────────────────────────────────
        if (data.auth_type && data.auth_config) {
          try {
            const cfg =
              typeof data.auth_config === 'string'
                ? JSON.parse(data.auth_config)
                : data.auth_config;
            if (data.auth_type === 'bearer') {
              this.formdata.get('auth_token')?.setValue(cfg.token || '');
            } else if (data.auth_type === 'basic') {
              this.formdata.get('auth_username')?.setValue(cfg.username || '');
              this.formdata.get('auth_password')?.setValue(cfg.password || '');
            } else if (data.auth_type === 'api_key') {
              this.formdata.get('auth_key_value')?.setValue(cfg.key || '');
              this.formdata
                .get('auth_header_name')
                ?.setValue(cfg.header_name || '');
            }
          } catch (_) {}
        }

        // ── Parse schedule_value ──────────────────────────────────────────────
        if (data.schedule_type && data.schedule_value) {
          const sv =
            typeof data.schedule_value === 'string'
              ? JSON.parse(data.schedule_value)
              : data.schedule_value;
          if (data.schedule_type === 'every_n_minutes' && sv.minutes) {
            this.formdata.get('schedule_minutes')?.setValue(sv.minutes);
          } else if (data.schedule_type === 'every_n_hours' && sv.hours) {
            this.formdata.get('schedule_hours')?.setValue(sv.hours);
          } else if (data.schedule_type === 'fixed_times' && sv.times) {
            this.formdata.get('schedule_times')?.setValue(sv.times);
          } else if (data.schedule_type === 'custom_cron' && sv.cron) {
            this.formdata.get('schedule_cron')?.setValue(sv.cron);
          }
        }

        // ── Rebuild fixed field_mapping rows ──────────────────────────────────
        this.updateFieldMappingBasedOnPunch(data.single_punch);
        const isSinglePunch = !!data.single_punch;
        const fixedKeys = isSinglePunch
          ? [
              'main_array_key',
              'employeecode',
              'date',
              'checkin_time',
              'checkout_time',
            ]
          : ['main_array_key', 'employeecode', 'date', 'punch_array'];

        (data.field_mapping || []).forEach((section: any) => {
          const standardKey = section.standard_field_key?.toLowerCase();
          if (!fixedKeys.includes(standardKey)) return;

          const rowIndex = this.field_mapping_array.controls.findIndex(
            (c) => c.get('standard_field_key')?.value === standardKey,
          );
          if (rowIndex < 0) return;
          const row = this.field_mapping_array.at(rowIndex) as FormGroup;

          if (standardKey === 'punch_array') {
            row.patchValue({
              company_field_key: Array.isArray(section.company_field_key)
                ? section.company_field_key
                : [],
              company_field_key_format:
                section.company_field_key_format || null,
            });
          } else {
            row.patchValue({
              company_field_key: section.company_field_key || '',
              company_field_key_format:
                section.company_field_key_format || null,
              standard_field_key_format:
                section.standard_field_key_format || null,
            });
          }
        });

        // ── Populate optional rows from non-fixed field_mapping entries ────────
        const optArray = this.formdata.get(
          'optional_field_mapping',
        ) as FormArray;
        this.clearFormArray(optArray);
        (data.field_mapping || []).forEach((section: any) => {
          const key = section.standard_field_key?.toLowerCase();
          if (FIXED_STANDARD_KEYS.includes(key)) return;
          const isPunchArray = key === 'punch_array';
          optArray.push(
            this.fb.group({
              standard_field_key: [
                section.standard_field_key,
                Validators.required,
              ],
              company_field_key: [
                isPunchArray
                  ? Array.isArray(section.company_field_key)
                    ? section.company_field_key
                    : []
                  : section.company_field_key || '',
                Validators.required,
              ],
              company_field_key_format: [
                section.company_field_key_format || null,
              ],
              standard_field_key_format: [
                section.standard_field_key_format || null,
              ],
              company_field_key_delimiter: [
                section.company_field_key_delimiter || ',',
              ],
            }),
          );
        });

        // ── Patch value_mapping ───────────────────────────────────────────────
        const valueMappingArray = this.formdata.get(
          'value_mapping',
        ) as FormArray;
        this.clearFormArray(valueMappingArray);
        if (data.value_mapping && Array.isArray(data.value_mapping)) {
          data.value_mapping.forEach((vm: any) => {
            valueMappingArray.push(
              this.fb.group({
                vendor_value: [vm.vendor_value || '', Validators.required],
                system_value: [vm.system_value || '', Validators.required],
              }),
            );
          });
        }

        // ── Reset form tracker ────────────────────────────────────────────────
        this.formChangeService.resetForm(this.formKey, this.formdata);
        this.isFormChanged();
      },
      error: (error) => {
        console.error('Error fetching view data:', error);
      },
    });
  }

  clearFormArray(formArray: FormArray) {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }

  isFormChanged() {
    this.formisDisable = this.formChangeService.hasChanged(
      this.formKey,
      this.formdata,
    );
  }

  private logInvalidFields(
    formGroup: FormGroup | FormArray,
    parentKey: string = '',
  ): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.logInvalidFields(control, fullKey); // recursive for nested forms
      } else if (control && control.invalid) {
        console.log(`❌ Invalid field: ${fullKey}`, control.errors);
      }
    });
  }

  formSubmit(): void {
    this.saveClick = true;

    if (this.formdata.valid) {
      const raw = this.formdata.getRawValue();

      // ── Build auth_config ───────────────────────────────────────────────────
      let authConfig: string | null = null;
      if (raw.auth_type === 'bearer') {
        authConfig = JSON.stringify({ token: raw.auth_token });
      } else if (raw.auth_type === 'basic') {
        authConfig = JSON.stringify({
          username: raw.auth_username,
          password: raw.auth_password,
        });
      } else if (raw.auth_type === 'api_key') {
        authConfig = JSON.stringify({
          key: raw.auth_key_value,
          header_name: raw.auth_header_name,
        });
      }

      // ── Build schedule_value ────────────────────────────────────────────────
      let scheduleValue: any = null;
      if (raw.schedule_type === 'every_n_minutes') {
        scheduleValue = { minutes: raw.schedule_minutes };
      } else if (raw.schedule_type === 'every_n_hours') {
        scheduleValue = { hours: raw.schedule_hours };
      } else if (raw.schedule_type === 'fixed_times') {
        scheduleValue = { times: raw.schedule_times };
      } else if (raw.schedule_type === 'custom_cron') {
        scheduleValue = { cron: raw.schedule_cron };
      }

      // ── Build company object ────────────────────────────────────────────────
      const selectedCompany = this.companyList.find(
        (c: any) => c.id === raw.company,
      );
      const companyObj = selectedCompany
        ? {
            id: selectedCompany.id,
            company_name: selectedCompany.company_name,
            client_id: selectedCompany.client_id,
          }
        : null;

      // ── Merge fixed + optional field_mapping ────────────────────────────────
      const fixedMappings = (raw.field_mapping || []).map((row: any) => {
        const entry: any = {
          standard_field_key: row.standard_field_key,
          company_field_key: row.company_field_key,
        };
        if (row.company_field_key_format)
          entry.company_field_key_format = row.company_field_key_format;
        if (row.standard_field_key_format)
          entry.standard_field_key_format = row.standard_field_key_format;
        return entry;
      });

      const optionalMappings = (raw.optional_field_mapping || []).map(
        (row: any) => {
          const entry: any = {
            standard_field_key: row.standard_field_key,
            company_field_key: row.company_field_key,
          };
          if (row.company_field_key_format)
            entry.company_field_key_format = row.company_field_key_format;
          if (row.standard_field_key_format)
            entry.standard_field_key_format = row.standard_field_key_format;
          if (
            row.standard_field_key === 'tna_io' &&
            raw.value_mapping?.length
          ) {
            entry.value_mapping = raw.value_mapping;
          }
          if (
            row.standard_field_key === 'punch_csv' &&
            row.company_field_key_delimiter
          ) {
            entry.company_field_key_delimiter = row.company_field_key_delimiter;
          }
          return entry;
        },
      );

      const payload: any = {
        name: raw.name,
        company: companyObj,
        api_url: raw.api_url,
        http_method: raw.http_method || 'GET',
        api_key: raw.api_key || '',
        auth_type: raw.auth_type || null,
        auth_config: authConfig,
        branch_name: raw.branch_name || '',
        location: raw.location_name || '',
        schedule_type: raw.schedule_type || null,
        schedule_value: scheduleValue,
        field_mapping: [...fixedMappings, ...optionalMappings],
        value_mapping: raw.value_mapping || [],
        single_punch: raw.single_punch ? 1 : 0,
        type: raw.type,
        status: raw.status ? 1 : 0,
        device_ids: raw.device_ids || [],
      };

      const apiCall = !this.paramValue
        ? this.fieldMappingService.createFieldMapping(payload)
        : this.fieldMappingService.updateFieldMapping(payload, this.paramValue);

      apiCall.subscribe({
        next: (resp: any) => {
          const action = !this.paramValue ? 'created' : 'updated';
          this.toastService.success(`Field mapping ${action} successfully`);
          this.formChangeService.resetForm(this.formKey, this.formdata);
          this.formisDisable = false;
          this.router.navigate(['/app/attendance/field-mapping']);
        },
        error: (err: { error: { message: string } }) => {
          this.toastService.failure(err.error.message);
        },
      });
    } else {
      this.markFormGroupTouched();
      this.logInvalidFields(this.formdata);
      this.toastService.failure('Please fill all required fields');
    }
  }

  private markFormGroupTouched(): void {
    // Recursively mark all controls (including nested FormArray/FormGroup) as touched
    const markTouched = (control: any) => {
      if (!control) return;
      if (control.controls) {
        Object.keys(control.controls).forEach((key) => {
          markTouched(control.controls[key]);
        });
      }
      try {
        control.markAsTouched({ onlySelf: true });
      } catch (e) {
        // ignore
      }
    };
    markTouched(this.formdata);
  }

  isInvalid(controlName: string): boolean {
    const control = this.formdata.get(controlName);
    if (!control) return false;

    return (
      (this.saveClick && control.invalid) ||
      (this.saveClick && !!control.errors) ||
      (control.invalid && (control.dirty || control.touched))
    );
  }

  checkPermission(type: string): boolean {
    return this.auth.checkPermission(type);
  }

  onCancel(): void {
    this.location.back();
  }

  private updateFieldMappingBasedOnPunch(singlePunch: any): void {
    const sectionsArray = this.formdata.get('field_mapping') as FormArray;
    sectionsArray.clear();

    // Define field structure based on punch type
    const fields = !singlePunch
      ? [
          {
            standard_field_key: 'main_array_key',
            standard_field_name: 'Main Array Key',
            standard_field_key_format: null,
          },
          {
            standard_field_key: 'employeecode',
            standard_field_name: 'Employee Code',
            standard_field_key_format: null,
          },
          {
            standard_field_key: 'date',
            standard_field_name: 'Date',
            standard_field_key_format: 'YYYY-MM-DD',
          },
          {
            standard_field_key: 'punch_array',
            standard_field_name: 'Punches',
            standard_field_key_format: 'HH:mm',
          },
        ]
      : [
          {
            standard_field_key: 'main_array_key',
            standard_field_name: 'Main Array Key',
            standard_field_key_format: null,
          },
          {
            standard_field_key: 'employeecode',
            standard_field_name: 'Employee Code',
            standard_field_key_format: null,
          },
          {
            standard_field_key: 'date',
            standard_field_name: 'Date',
            standard_field_key_format: 'YYYY-MM-DD',
          },
          {
            standard_field_key: 'checkin_time',
            standard_field_name: 'Check-In Time',
            standard_field_key_format: 'HH:mm',
          },
          {
            standard_field_key: 'checkout_time',
            standard_field_name: 'Check-Out Time',
            standard_field_key_format: 'HH:mm',
          },
        ];

    // Loop and apply validation rules
    fields.forEach((field) => {
      const standardKey = field.standard_field_key;
      const isPunchArray = standardKey === 'punch_array';
      const needsFormat = !NO_FORMAT_KEYS.includes(standardKey);
      const fmtValidators = needsFormat ? [Validators.required] : [];

      sectionsArray.push(
        this.fb.group({
          standard_field_name: [field.standard_field_name, Validators.required],
          standard_field_key: [field.standard_field_key, Validators.required],
          standard_field_key_format: [
            field.standard_field_key_format,
            fmtValidators,
          ],
          company_field_key: [
            isPunchArray ? [] : '',
            isPunchArray
              ? [Validators.required]
              : [Validators.required, Validators.pattern(/^[^\s]+$/)],
          ],
          company_field_key_format: [null, fmtValidators],
        }),
      );
    });

    // Reset shared input and clear touched state
    this.formdata.get('company_field_key_input')?.reset();
    sectionsArray.controls.forEach((ctrl) => {
      const k = ctrl.get('company_field_key');
      if (k) {
        try {
          k.markAsUntouched();
          k.updateValueAndValidity({ onlySelf: true });
        } catch (e) {}
      }
    });
  }

  addItem(): void {
    const form = this.formdata;
    const inputControl = form.get('device_id_input') as FormControl;
    const rawValue = inputControl?.value;
    const value = rawValue ? String(rawValue).trim() : '';

    // Basic presence check
    if (!value) {
      this.toastService.failure('Please enter a value');
      return;
    }

    // Validate using form control validators (no spaces, max 30 chars)
    if (inputControl && inputControl.invalid) {
      this.toastService.failure('Invalid format: no spaces allowed');
      return;
    }

    const currentValues = form.get('device_ids')?.value ?? [];
    if (!currentValues.includes(value)) {
      currentValues.push(value);
      form.get('device_ids')?.setValue(currentValues);
      form.get('device_ids')?.markAsDirty();
    } else {
      this.toastService.failure('Value already exists');
    }

    form.get('device_id_input')?.reset();
  }

  removeItem(index: number): void {
    const array = this.formdata.get('device_ids')?.value ?? [];
    array.splice(index, 1);
    this.formdata.get('device_ids')?.setValue(array);
    this.formdata.get('device_ids')?.markAsTouched();
    this.formdata.get('device_ids')?.updateValueAndValidity();
  }

  // ----- Punch array (per-row) helper methods -----
  addPunchItem(rowIndex: number): void {
    const sectionsArray = this.formdata.get('field_mapping') as FormArray;
    const row = sectionsArray.at(rowIndex) as FormGroup;
    if (!row) return;

    const inputControl = this.formdata.get(
      'company_field_key_input',
    ) as FormControl;
    const rawValue = inputControl?.value;
    const value = rawValue ? String(rawValue).trim() : '';

    // Basic presence check
    if (!value) {
      this.toastService.failure('Please enter a value');
      return;
    }

    // Validate using control validators (no spaces, max 30 chars)
    if (inputControl && inputControl.invalid) {
      this.toastService.failure('Invalid format: no spaces allowed');
      return;
    }

    const currentValues = row.get('company_field_key')?.value ?? [];
    if (!Array.isArray(currentValues)) {
      // normalize to array
      row.get('company_field_key')?.setValue([]);
    }
    const arr: any[] = row.get('company_field_key')?.value ?? [];
    if (!arr.includes(value)) {
      arr.push(value);
      row.get('company_field_key')?.setValue(arr);
      row.get('company_field_key')?.markAsDirty();
    } else {
      this.toastService.failure('Value already exists');
    }

    this.formdata.get('company_field_key_input')?.reset();
  }

  removePunchItem(rowIndex: number, index: number): void {
    const sectionsArray = this.formdata.get('field_mapping') as FormArray;
    const row = sectionsArray.at(rowIndex) as FormGroup;
    if (!row) return;

    const array = row.get('company_field_key')?.value ?? [];
    if (!Array.isArray(array)) return;
    array.splice(index, 1);
    row.get('company_field_key')?.setValue(array);
    row.get('company_field_key')?.markAsTouched();
    row.get('company_field_key')?.updateValueAndValidity();
  }
}
